import { useEffect, useMemo, useReducer } from 'react';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import {
    ethylene,
    EthyleneConcentration,
    FLUID_TEMPERATURES,
    MediumTypesEnum, propylene, PropyleneConcentration,
    water,
} from 'src/modules/user-input/calcV100/constants.ts';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { getConcentrations } from 'src/modules/user-input/calcV100/getConcentrations.ts';
import { Units } from 'uom-units';
import { DecimalField } from 'src/modules/common/components/DecimalField/DecimalField.tsx';
import { reducer } from 'src/modules/user-input/MediumParams/reducer.ts';
import { MediumParamsProps, MediumParamsState } from 'src/modules/user-input/MediumParams/types.ts';
import { UnitSelector } from 'src/modules/user-input/components/UnitSelector.tsx';
import { DropdownField } from 'src/modules/common/components/DropdownField/DropdownField.tsx';
import { RadioButton } from 'src/modules/common/components/Radio/RadioButton.tsx';
import { convert } from 'src/modules/user-input/convert.ts';
import { RegionsEnum } from 'src/generated/operations.ts';
import { RenderTemperature } from 'src/modules/user-input/components/RenderTemperature.tsx';
import clsx from 'clsx';

const initialStateEU: MediumParamsState = {
    medium: MediumTypesEnum.Water,
    temperature: 20,
    concentration: '20',
    density: '',
    densityUnit: Units.KilogramPerCubicMeter,
    heatCapacity: '',
    heatCapacityUnit: Units.KilojoulePerKilogramKelvin,
};

const MEDIUMS = [
    MediumTypesEnum.Water,
    MediumTypesEnum.Ethylene,
    MediumTypesEnum.Propylene,
    MediumTypesEnum.Other,
];

export const MediumParams = ({
    currentRegion,
    currentLocale,
    onChange,
    allowedUnits,
    uuid,
    showHeatCapacity = true,
} : MediumParamsProps) => {
    const initialState: MediumParamsState = {
        ...initialStateEU,
        ...(currentRegion === RegionsEnum.Am ? {
            densityUnit: allowedUnits.density[0],
            heatCapacityUnit: allowedUnits.heatCapacity[0],
        } : {}),
    };

    const renderTemperature = (label: number) => RenderTemperature({
        label,
        sourceUnit: Units.Celsius,
        targetUnit: currentRegion === RegionsEnum.Am ? Units.Fahrenheit : Units.Celsius,
    });

    const [state, dispatch] = useReducer(reducer, { ...initialState });

    const calculatedValues = useMemo(() => {
        const tempIndex = FLUID_TEMPERATURES.findIndex((value) => value === state.temperature);

        const concentration = getConcentrations(state.medium).includes(state.concentration)
            ? state.concentration : getConcentrations(state.medium)[0] as EthyleneConcentration;

        let density = '';
        let heatCapacity = '';
        switch (state.medium) {
            case MediumTypesEnum.Propylene: {
                density = propylene.density[concentration as PropyleneConcentration][tempIndex];
                heatCapacity = propylene.heatCapacity[concentration as PropyleneConcentration][tempIndex];
                break;
            }
            case MediumTypesEnum.Ethylene: {
                density = ethylene.density[concentration as EthyleneConcentration][tempIndex];
                heatCapacity = ethylene.heatCapacity[concentration as EthyleneConcentration][tempIndex];
                break;
            }
            case MediumTypesEnum.Water: {
                density = water.density;
                heatCapacity = water.heatCapacity;
                break;
            }
            default: {
                // @NO-OP
            }
        }
        return {
            concentration,
            density: state.density.length > 0 ? state.density : convert(
                density,
                Units.KilogramPerCubicMeter,
                state.densityUnit,
            ),
            heatCapacity: state.heatCapacity.length > 0 ? state.heatCapacity : convert(
                heatCapacity,
                Units.KilojoulePerKilogramKelvin,
                state.heatCapacityUnit,
            ),
        };
    }, [state]);

    useEffect(() => {
        onChange({
            ...calculatedValues,
        });
    }, [calculatedValues, onChange]);

    return (
        <div className="vss-medium-params">
            <div className="vss-medium-params__radio-container">
                {MEDIUMS.map((medium) => (
                    <RadioButton
                        key={`${medium}`}
                        id={`${medium}-${uuid}`}
                        required={true}
                        value={medium}
                        checked={medium === state.medium}
                        onChange={() => {
                            dispatch({
                                type: 'medium_change',
                                payload: medium,
                            });
                        }}
                    >
                        <I18n messageId={medium}/>
                    </RadioButton>
                ))}
            </div>

            <div className="vss-medium-params__input-container">
                {state.medium !== MediumTypesEnum.Other && (
                    <>
                        <DropdownField
                            size="lg"
                            disabled={state.medium === MediumTypesEnum.Water}
                            options={state.medium === MediumTypesEnum.Water ? [20] : FLUID_TEMPERATURES}
                            selectedValue={state.temperature}
                            renderOption={(label) => renderTemperature(label)}
                            onChange={(payload) => {
                                dispatch({
                                    type: 'temperature_change',
                                    payload,
                                });
                            }}
                        >
                            <I18n messageId={MESSAGES.FLUID_TEMPERATURE}/>
                        </DropdownField>

                        <DropdownField
                            size="lg"
                            disabled={state.medium === MediumTypesEnum.Water}
                            options={state.medium === MediumTypesEnum.Water ? ['0'] : getConcentrations(state.medium)}
                            selectedValue={state.medium === MediumTypesEnum.Water
                                ? '0' : calculatedValues.concentration}
                            renderOption={(label) => (<>{label} %</>)}
                            onChange={(payload) => {
                                dispatch({
                                    type: 'concentration_change',
                                    payload,
                                });
                            }}
                        >
                            <I18n messageId={MESSAGES.Concentration}/>
                        </DropdownField>
                    </>
                )}

                <DecimalField
                    inputSize="lg"
                    name="density"
                    className={clsx({
                        'vss-medium-params__density--only': state.medium === MediumTypesEnum.Other && !showHeatCapacity,
                    })}
                    labelText={MESSAGES.Density}
                    disabled={state.medium !== MediumTypesEnum.Other}
                    value={calculatedValues.density}
                    endAdornment={(
                        <UnitSelector
                            disabled={state.medium !== MediumTypesEnum.Other}
                            selected={state.densityUnit.name}
                            values={allowedUnits.density}
                            onChange={(payload) => {
                                dispatch({ type: 'density_unit', payload });
                            }}
                        />
                    )}
                    currentLocale={currentLocale}
                    onValueChange={(payload) => {
                        dispatch({
                            type: 'density_change',
                            payload,
                        });
                    }}
                    autoComplete="off"
                />

                {showHeatCapacity && (
                    <DecimalField
                        inputSize="lg"
                        name="heatCapacity"
                        labelText={MESSAGES.SPEC_HEAT_CAP}
                        disabled={state.medium !== MediumTypesEnum.Other}
                        value={calculatedValues.heatCapacity}
                        endAdornment={(
                            <UnitSelector
                                disabled={state.medium !== MediumTypesEnum.Other}
                                selected={state.heatCapacityUnit.name}
                                values={allowedUnits.heatCapacity}
                                onChange={(payload) => {
                                    dispatch({ type: 'specificHeatCapacity_unit', payload });
                                }}
                            />
                        )}
                        currentLocale={currentLocale}
                        onValueChange={(payload) => {
                            dispatch({
                                type: 'specificHeatCapacity_change',
                                payload,
                            });
                        }}
                        autoComplete="off"
                    />
                )}
            </div>
        </div>
    );
};
